import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react';
import RoomSelector from './RoomSelector';
import html2canvas from 'html2canvas';
import chair from '../images/sidebar_chair.svg';
import conferenceTable from '../images/conference_table.svg';
import smallTable from '../images/small_table.svg';
import deleteIcon from '../images/delete-icon.svg';
import rotateIcon from '../images/rotate-icon.svg';
import { useSelectionManager, SelectionBoxView } from './SelectionManager';
import './RoomLayout.css';
import LayoutSaver from './LayoutSaver';
import { getRoomImagePath } from './RoomConfig';

const WallJFF = () => (
  <rect x="0.5" y="0.5" width="10" height="149" fill="#9FC2E0" stroke="#1A5E9C" />
);

const DashedWallJFF = () => (
  <rect x="0.5" y="0.5" width="10" height="149" fill="white" stroke="#0062BB" strokeDasharray="3 3" />
);

const WallJKP = () => (
  <rect x="0.5" y="0.5" width="10" height="147" fill="#9FC2E0" stroke="#1A5E9C" />
);

const DashedWallJKP = () => (
  <rect x="0.5" y="0.5" width="10" height="147" fill="white" stroke="#0062BB" strokeDasharray="3 3" />
);


const ConferenceTable = () => (
  <rect x="0.5" y="0.5" width="28" height="78" rx="3.5" fill="#D9D9D9" stroke="black" />
);

const SmallTable = () => (
  <rect x="0.5" y="0.5" width="36" height="36" rx="3.5" fill="#D9D9D9" stroke="black" />
);

const Chair = () => (
  <g id="chair">
    <rect id="Rectangle 8" x="1.07895" y="1.07895" width="19.6842" height="20.8421" rx="9.84211" fill="#D9D9D9" stroke="black" strokeWidth="1.15789" />
    <path id="Rectangle 9" d="M4.55258 10.8792C4.55258 7.59226 7.05403 4.84534 10.3266 4.53854L17.4901 3.86696C19.8673 3.64409 21.921 5.51427 21.921 7.90189V15.0981C21.921 17.4857 19.8673 19.3559 17.4901 19.1331L10.3266 18.4615C7.05403 18.1547 4.55258 15.4077 4.55258 12.1209V10.8792Z" fill="#D9D9D9" stroke="black" strokeWidth="1.15789" />
  </g>
);

const InstructPopup = () => {
  const [isInstructOpen, setIsInstructOpen] = useState(false);

  const toggleInstruct = () => {
    setIsInstructOpen(!isInstructOpen);
  };

  return (
    <>
      {/*Instruction Button*/}
      <button
        className="instruct-button"
        onClick={toggleInstruct}
        aria-label="Help"
      >
        ?
      </button>
      {/* Popup Modal */}
      {isInstructOpen && (
        <div className="popup-overlay" onClick={toggleInstruct}>
          <div
            className="popup-content"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
          >
            <h1>Instructions</h1>
            <h2>Welcome to the ELC Room Layout Arranger!</h2>
            <p>Please refer to the following instructions when using the layout arranger:</p>

            <ul>
            <li><strong>Default layout:</strong> This will auto-populate all rooms of the selected ELC with the default chair and table layout</li>
            <li><strong>Audience style:</strong> This will auto-populate all rooms of the selected ELC with a Chair Only audience room style</li>
            <li><strong>Small Table Audience Style (JFF Only):</strong> This will auto-populate JFF rooms with an audience style that includes small tables</li>
            <li><strong>Furniture buttons:</strong> Clicking the chair, small table, and conference table buttons will cause each furniture piece to appear on the layout. The number shown is the number of remaining available pieces of furniture</li>
            <li><strong>Delete:</strong> This will delete the selected furniture</li>
            <li><strong>Rotate:</strong> This will rotate the selected furniture 90° at a time</li>
            <li><strong>Clear Layout:</strong> This will clear the entire layout of the selected ELC</li>
            <li><strong>Download Layouts:</strong> This will download PNGs of both room layouts to a zip file that will be saved on your device</li>
            </ul>
            <h3>CONTROLS</h3>
              <ul class="with-bullets">
                <li>Select all desired furniture using drag and highlight or shift-click</li>
                <li>You can also delete by clicking the delete key</li>
              </ul>
            
            <button onClick={toggleInstruct} className="close-button">
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const getRoomImage = (roomId) => getRoomImagePath(roomId);

const getInitialRoomState = (roomId) => {
  if (roomId === 'room1') return INITIAL_ROOM_STATE_JFF;
  if (roomId === 'room2') return INITIAL_ROOM_STATE_JKP;
};

const INITIAL_ROOM_STATE_JFF = {
  furniture: [],
  chairNumber: 138,
  tableNumber: 12,
  smallTableNumber: 12,
  walls: []
};

const INITIAL_ROOM_STATE_JKP = {
  furniture: [],
  chairNumber: 120,
  tableNumber: 15, // JKP has no small tables
  smallTableNumber: 0,
  walls: []
};

const wallLayout = {
  room1: [
    { type: 'walljff', x: 218, y: 30, visible: true },
    { type: 'walljff', x: 338, y: 30, visible: true },
    { type: 'walljff', x: 463, y: 30, visible: true },
    { type: 'walljff', x: 579, y: 30, visible: true },
    { type: 'walljff', x: 713, y: 30, visible: true },
    { type: 'walljff', x: 218, y: 300, visible: true },
    { type: 'walljff', x: 341, y: 300, visible: true },
    { type: 'walljff', x: 463, y: 300, visible: true },
    { type: 'walljff', x: 579, y: 300, visible: true },
    { type: 'walljff', x: 714, y: 300, visible: true },
  ],
  room2: [
    { type: 'walljkp', x: 744, y: 138, visible: true },
    { type: 'walljkp', x: 876, y: 138, visible: true },
    { type: 'walljkp', x: 743, y: 402, visible: true },
    { type: 'walljkp', x: 877, y: 402, visible: true },
  ]
}

// Define default layouts for each room type
const defaultLayouts = {
  room1: [ // JFF Basement
    // Room A
    { type: 'chair', x: 133, y: 54 },
    { type: 'chair', x: 133, y: 80 },
    { type: 'chair', x: 133, y: 105 },
    { type: 'table', x: 153, y: 48, rotation: 180 },
    { type: 'chair', x: 193, y: 54, rotation: 180 },
    { type: 'chair', x: 193, y: 80, rotation: 180 },
    { type: 'chair', x: 193, y: 105, rotation: 180},
    { type: 'chair', x: 188, y: 150, rotation: 270},
    { type: 'chair', x: 163, y: 150, rotation: 270},

    // Room B
    { type: 'chair', x: 245, y: 54 },
    { type: 'chair', x: 245, y: 80 },
    { type: 'chair', x: 245, y: 105 },
    { type: 'table', x: 265, y: 48, rotation: 180 },
    { type: 'chair', x: 305, y: 54, rotation: 180 },
    { type: 'chair', x: 305, y: 80, rotation: 180 },
    { type: 'chair', x: 305, y: 105, rotation: 180 },
    { type: 'chair', x: 260, y: 150, rotation: 270 },
    { type: 'chair', x: 235, y: 150, rotation: 270 },

    // Room C
    { type: 'chair', x: 369, y: 54 },
    { type: 'chair', x: 369, y: 80 },
    { type: 'chair', x: 369, y: 105 },
    { type: 'table', x: 389, y: 48, rotation: 180 },
    { type: 'chair', x: 429, y: 54, rotation: 180 },
    { type: 'chair', x: 429, y: 80, rotation: 180 },
    { type: 'chair', x: 429, y: 105, rotation: 180 },
    { type: 'chair', x: 424, y: 150, rotation: 270 },
    { type: 'chair', x: 399, y: 150, rotation: 270 },

    // Room D
    { type: 'chair', x: 491, y: 54 },
    { type: 'chair', x: 491, y: 80 },
    { type: 'chair', x: 491, y: 105 },
    { type: 'table', x: 511, y: 48, rotation: 180 },
    { type: 'chair', x: 551, y: 54, rotation: 180 },
    { type: 'chair', x: 551, y: 80, rotation: 180 },
    { type: 'chair', x: 551, y: 105, rotation: 180 },
    { type: 'chair', x: 506, y: 150, rotation: 270 },
    { type: 'chair', x: 481, y: 150, rotation: 270 },

    // Room E
    { type: 'chair', x: 614, y: 54 },
    { type: 'chair', x: 614, y: 80 },
    { type: 'chair', x: 614, y: 105 },
    { type: 'table', x: 634, y: 48, rotation: 180 },
    { type: 'chair', x: 674, y: 54, rotation: 180 },
    { type: 'chair', x: 674, y: 80, rotation: 180 },
    { type: 'chair', x: 674, y: 105, rotation: 180 },
    { type: 'chair', x: 669, y: 150, rotation: 270 },
    { type: 'chair', x: 644, y: 150, rotation: 270 },

    // Room F
    { type: 'chair', x: 728, y: 54 },
    { type: 'chair', x: 728, y: 80 },
    { type: 'chair', x: 728, y: 105 },
    { type: 'table', x: 748, y: 48, rotation: 180 },
    { type: 'chair', x: 788, y: 54, rotation: 180 },
    { type: 'chair', x: 788, y: 80, rotation: 180 },
    { type: 'chair', x: 788, y: 105, rotation: 180 },
    { type: 'chair', x: 755, y: 150, rotation: 270 },
    { type: 'chair', x: 730, y: 150, rotation: 270 },

    // Room N
    { type: 'chair', x: 133, y: 350 },
    { type: 'chair', x: 133, y: 376 },
    { type: 'chair', x: 133, y: 401 },
    { type: 'table', x: 153, y: 344, rotation: 180 },
    { type: 'chair', x: 193, y: 350, rotation: 180 },
    { type: 'chair', x: 193, y: 376, rotation: 180 },
    { type: 'chair', x: 193, y: 401, rotation: 180 },
    { type: 'chair', x: 188, y: 306, rotation: 90 },
    { type: 'chair', x: 163, y: 306, rotation: 90 },

    // Room M
    { type: 'chair', x: 245, y: 350 },
    { type: 'chair', x: 245, y: 376 },
    { type: 'chair', x: 245, y: 401 },
    { type: 'table', x: 265, y: 344, rotation: 180 },
    { type: 'chair', x: 305, y: 350, rotation: 180 },
    { type: 'chair', x: 305, y: 376, rotation: 180 },
    { type: 'chair', x: 305, y: 401, rotation: 180 },
    { type: 'chair', x: 260, y: 306, rotation: 90 },
    { type: 'chair', x: 235, y: 306, rotation: 90 },

    // Room L
    { type: 'chair', x: 369, y: 350 },
    { type: 'chair', x: 369, y: 376 },
    { type: 'chair', x: 369, y: 401 },
    { type: 'table', x: 389, y: 344, rotation: 180 },
    { type: 'chair', x: 429, y: 350, rotation: 180 },
    { type: 'chair', x: 429, y: 376, rotation: 180 },
    { type: 'chair', x: 429, y: 401, rotation: 180 },
    { type: 'chair', x: 424, y: 306, rotation: 90 },
    { type: 'chair', x: 399, y: 306, rotation: 90 },

    // Room K
    { type: 'chair', x: 491, y: 350 },
    { type: 'chair', x: 491, y: 376 },
    { type: 'chair', x: 491, y: 401 },
    { type: 'table', x: 511, y: 344, rotation: 180 },
    { type: 'chair', x: 551, y: 350, rotation: 180 },
    { type: 'chair', x: 551, y: 376, rotation: 180 },
    { type: 'chair', x: 551, y: 401, rotation: 180 },
    { type: 'chair', x: 506, y: 306, rotation: 90 },
    { type: 'chair', x: 481, y: 306, rotation: 90 },

    // Room J
    { type: 'chair', x: 614, y: 350 },
    { type: 'chair', x: 614, y: 376 },
    { type: 'chair', x: 614, y: 401 },
    { type: 'table', x: 634, y: 344, rotation: 180 },
    { type: 'chair', x: 674, y: 350, rotation: 180 },
    { type: 'chair', x: 674, y: 376, rotation: 180 },
    { type: 'chair', x: 674, y: 401, rotation: 180 },
    { type: 'chair', x: 669, y: 306, rotation: 90 },
    { type: 'chair', x: 644, y: 306, rotation: 90 },

    // Room G
    { type: 'chair', x: 728, y: 350 },
    { type: 'chair', x: 728, y: 376 },
    { type: 'chair', x: 728, y: 401 },
    { type: 'table', x: 748, y: 344, rotation: 180 },
    { type: 'chair', x: 788, y: 350, rotation: 180 },
    { type: 'chair', x: 788, y: 376, rotation: 180 },
    { type: 'chair', x: 788, y: 401, rotation: 180 },
    { type: 'chair', x: 755, y: 306, rotation: 90 },
    { type: 'chair', x: 730, y: 306, rotation: 90 },


  ],
  room2: [ // JKP Merged
    // 201F
    { type: 'chair', x: 58, y: 95 },
    { type: 'chair', x: 58, y: 118 },
    { type: 'chair', x: 58, y: 141 },
    { type: 'chair', x: 118, y: 95, rotation: 180},
    { type: 'chair', x: 118, y: 118, rotation: 180},
    { type: 'chair', x: 118, y: 141, rotation: 180},
    { type: 'table', x: 85, y: 90},
    { type: 'chair', x: 105, y: 197, rotation: 270},
    { type: 'chair', x: 130, y: 197, rotation: 270},

    // 201G
    { type: 'chair', x: 198, y: 95 },
    { type: 'chair', x: 198, y: 118 },
    { type: 'chair', x: 198, y: 141 },
    { type: 'chair', x: 258, y: 95, rotation: 180 },
    { type: 'chair', x: 258, y: 118, rotation: 180 },
    { type: 'chair', x: 258, y: 141, rotation: 180 },
    { type: 'table', x: 225, y: 90 },
    { type: 'chair', x: 175, y: 197, rotation: 270 },
    { type: 'chair', x: 200, y: 197, rotation: 270 },
    
    // 201E
    { type: 'chair', x: 65, y: 415 },
    { type: 'chair', x: 65, y: 438 },
    { type: 'chair', x: 65, y: 461 },
    { type: 'chair', x: 125, y: 415, rotation: 180 },
    { type: 'chair', x: 125, y: 438, rotation: 180 },
    { type: 'chair', x: 125, y: 461, rotation: 180 },
    { type: 'table', x: 92, y: 410 },
    { type: 'chair', x: 95, y: 360, rotation: 90 },
    { type: 'chair', x: 121, y: 360, rotation: 90 },
    
    // 201D
    { type: 'chair', x: 215, y: 415 },
    { type: 'chair', x: 215, y: 438 },
    { type: 'chair', x: 215, y: 461 },
    { type: 'chair', x: 275, y: 415, rotation: 180 },
    { type: 'chair', x: 275, y: 438, rotation: 180 },
    { type: 'chair', x: 275, y: 461, rotation: 180 },
    { type: 'table', x: 242, y: 410 },
    { type: 'chair', x: 187, y: 360, rotation: 90 },
    { type: 'chair', x: 215, y: 360, rotation: 90 },

    // 301K
    { type: 'chair', x: 372, y: 45, rotation: 90 },
    { type: 'chair', x: 396, y: 45, rotation: 90 },
    { type: 'chair', x: 420, y: 45, rotation: 90 },
    { type: 'chair', x: 372, y: 100, rotation: 270 },
    { type: 'chair', x: 396, y: 100, rotation: 270 },
    { type: 'chair', x: 420, y: 100, rotation: 270 },
    { type: 'table', x: 387, y: 47, rotation: 90 },
    { type: 'chair', x: 468, y: 42, rotation: 180 },
    { type: 'chair', x: 468, y: 70, rotation: 180 },

    // 301J
    { type: 'chair', x: 384, y: 176, rotation: 90 },
    { type: 'chair', x: 408, y: 176, rotation: 90 },
    { type: 'chair', x: 432, y: 176, rotation: 90 },
    { type: 'chair', x: 384, y: 235, rotation: 270 },
    { type: 'chair', x: 408, y: 235, rotation: 270 },
    { type: 'chair', x: 432, y: 235, rotation: 270 },
    { type: 'table', x: 399, y: 180, rotation: 90 },
    { type: 'chair', x: 470, y: 207, rotation: 180 },
    { type: 'chair', x: 470, y: 235, rotation: 180 },

    // 301G
    { type: 'chair', x: 369, y: 443, rotation: 90 },
    { type: 'chair', x: 393, y: 443, rotation: 90 },
    { type: 'chair', x: 417, y: 443, rotation: 90 },
    { type: 'chair', x: 369, y: 502, rotation: 270 },
    { type: 'chair', x: 393, y: 502, rotation: 270 },
    { type: 'chair', x: 417, y: 502, rotation: 270 },
    { type: 'table', x: 384, y: 447, rotation: 90 },
    { type: 'chair', x: 468, y: 479, rotation: 180 },
    { type: 'chair', x: 468, y: 507, rotation: 180 },
    
    // 301A
    { type: 'chair', x: 657, y: 160 },
    { type: 'chair', x: 657, y: 183 },
    { type: 'chair', x: 657, y: 206 },
    { type: 'chair', x: 717, y: 160, rotation: 180 },
    { type: 'chair', x: 717, y: 183, rotation: 180 },
    { type: 'chair', x: 717, y: 206, rotation: 180 },
    { type: 'table', x: 684, y: 155 },
    { type: 'chair', x: 688, y: 257, rotation: 270 },
    { type: 'chair', x: 713, y: 257, rotation: 270 },
    
    // 301B
    { type: 'chair', x: 763, y: 160 },
    { type: 'chair', x: 763, y: 183 },
    { type: 'chair', x: 763, y: 206 },
    { type: 'chair', x: 823, y: 160, rotation: 180 },
    { type: 'chair', x: 823, y: 183, rotation: 180 },
    { type: 'chair', x: 823, y: 206, rotation: 180 },
    { type: 'table', x: 790, y: 155 },
    { type: 'chair', x: 770, y: 257, rotation: 270 },
    { type: 'chair', x: 795, y: 257, rotation: 270 },
    
    // 301C
    { type: 'chair', x: 895, y: 160 },
    { type: 'chair', x: 895, y: 183 },
    { type: 'chair', x: 895, y: 206 },
    { type: 'chair', x: 955, y: 160, rotation: 180 },
    { type: 'chair', x: 955, y: 183, rotation: 180 },
    { type: 'chair', x: 955, y: 206, rotation: 180 },
    { type: 'table', x: 922, y: 155 },
    { type: 'chair', x: 897, y: 257, rotation: 270 },
    { type: 'chair', x: 922, y: 257, rotation: 270 },

    // 301F
    { type: 'chair', x: 654, y: 455 },
    { type: 'chair', x: 654, y: 478 },
    { type: 'chair', x: 654, y: 501 },
    { type: 'chair', x: 714, y: 455, rotation: 180 },
    { type: 'chair', x: 714, y: 478, rotation: 180 },
    { type: 'chair', x: 714, y: 501, rotation: 180 },
    { type: 'table', x: 681, y: 450 },
    { type: 'chair', x: 691, y: 405, rotation: 90 },
    { type: 'chair', x: 718, y: 405, rotation: 90 },

    // 301E
    { type: 'chair', x: 766, y: 455 },
    { type: 'chair', x: 766, y: 478 },
    { type: 'chair', x: 766, y: 501 },
    { type: 'chair', x: 826, y: 455, rotation: 180 },
    { type: 'chair', x: 826, y: 478, rotation: 180 },
    { type: 'chair', x: 826, y: 501, rotation: 180 },
    { type: 'table', x: 793, y: 450 },
    { type: 'chair', x: 773, y: 405, rotation: 90 },
    { type: 'chair', x: 800, y: 405, rotation: 90 },

    // 301D
    { type: 'chair', x: 900, y: 455 },
    { type: 'chair', x: 900, y: 478 },
    { type: 'chair', x: 900, y: 501 },
    { type: 'chair', x: 960, y: 455, rotation: 180 },
    { type: 'chair', x: 960, y: 478, rotation: 180 },
    { type: 'chair', x: 960, y: 501, rotation: 180 },
    { type: 'table', x: 927, y: 450 },
    { type: 'chair', x: 897, y: 405, rotation: 90 },
    { type: 'chair', x: 924, y: 405, rotation: 90 },
  ]
};

const audienceStyleLayouts = {
  room1: [
    // Room A
    { type: 'chair', x: 195, y: 150, rotation: 270},
    { type: 'chair', x: 170, y: 150, rotation: 270},
    { type: 'chair', x: 145, y: 150, rotation: 270},
    { type: 'chair', x: 195, y: 125, rotation: 270},
    { type: 'chair', x: 170, y: 125, rotation: 270},
    { type: 'chair', x: 145, y: 125, rotation: 270},
    { type: 'chair', x: 195, y: 100, rotation: 270},
    { type: 'chair', x: 170, y: 100, rotation: 270},

    // Room B
    { type: 'chair', x: 280, y: 150, rotation: 270 },
    { type: 'chair', x: 255, y: 150, rotation: 270 },
    { type: 'chair', x: 230, y: 150, rotation: 270 },
    { type: 'chair', x: 280, y: 125, rotation: 270 },
    { type: 'chair', x: 255, y: 125, rotation: 270 },
    { type: 'chair', x: 230, y: 125, rotation: 270 },
    { type: 'chair', x: 255, y: 100, rotation: 270 },
    { type: 'chair', x: 230, y: 100, rotation: 270 },
    
    // Room C
    { type: 'chair', x: 440, y: 150, rotation: 270 },
    { type: 'chair', x: 415, y: 150, rotation: 270 },
    { type: 'chair', x: 390, y: 150, rotation: 270 },
    { type: 'chair', x: 440, y: 125, rotation: 270 },
    { type: 'chair', x: 415, y: 125, rotation: 270 },
    { type: 'chair', x: 390, y: 125, rotation: 270 },
    { type: 'chair', x: 440, y: 100, rotation: 270 },
    { type: 'chair', x: 415, y: 100, rotation: 270 },

    // Room D
    { type: 'chair', x: 525, y: 150, rotation: 270 },
    { type: 'chair', x: 500, y: 150, rotation: 270 },
    { type: 'chair', x: 475, y: 150, rotation: 270 },
    { type: 'chair', x: 525, y: 125, rotation: 270 },
    { type: 'chair', x: 500, y: 125, rotation: 270 },
    { type: 'chair', x: 475, y: 125, rotation: 270 },
    { type: 'chair', x: 500, y: 100, rotation: 270 },
    { type: 'chair', x: 475, y: 100, rotation: 270 },

    // Room E
    { type: 'chair', x: 690, y: 150, rotation: 270 },
    { type: 'chair', x: 665, y: 150, rotation: 270 },
    { type: 'chair', x: 640, y: 150, rotation: 270 },
    { type: 'chair', x: 690, y: 125, rotation: 270 },
    { type: 'chair', x: 665, y: 125, rotation: 270 },
    { type: 'chair', x: 640, y: 125, rotation: 270 },
    { type: 'chair', x: 690, y: 100, rotation: 270 },
    { type: 'chair', x: 665, y: 100, rotation: 270 },

    // Room F
    { type: 'chair', x: 775, y: 150, rotation: 270 },
    { type: 'chair', x: 750, y: 150, rotation: 270 },
    { type: 'chair', x: 725, y: 150, rotation: 270 },
    { type: 'chair', x: 775, y: 125, rotation: 270 },
    { type: 'chair', x: 750, y: 125, rotation: 270 },
    { type: 'chair', x: 725, y: 125, rotation: 270 },
    { type: 'chair', x: 750, y: 100, rotation: 270 },
    { type: 'chair', x: 725, y: 100, rotation: 270 },

    // Room N
    { type: 'chair', x: 195, y: 356, rotation: 90 },
    { type: 'chair', x: 170, y: 356, rotation: 90 },
    { type: 'chair', x: 145, y: 306, rotation: 90 },
    { type: 'chair', x: 195, y: 331, rotation: 90 },
    { type: 'chair', x: 170, y: 331, rotation: 90 },
    { type: 'chair', x: 145, y: 331, rotation: 90 },
    { type: 'chair', x: 195, y: 306, rotation: 90 },
    { type: 'chair', x: 170, y: 306, rotation: 90 },

    // Room M
    { type: 'chair', x: 280, y: 306, rotation: 90 },
    { type: 'chair', x: 255, y: 356, rotation: 90 },
    { type: 'chair', x: 230, y: 356, rotation: 90 },
    { type: 'chair', x: 280, y: 331, rotation: 90 },
    { type: 'chair', x: 255, y: 331, rotation: 90 },
    { type: 'chair', x: 230, y: 331, rotation: 90 },
    { type: 'chair', x: 255, y: 306, rotation: 90 },
    { type: 'chair', x: 230, y: 306, rotation: 90 },

    // Room L
    { type: 'chair', x: 440, y: 356, rotation: 90 },
    { type: 'chair', x: 415, y: 356, rotation: 90 },
    { type: 'chair', x: 390, y: 306, rotation: 90 },
    { type: 'chair', x: 440, y: 331, rotation: 90 },
    { type: 'chair', x: 415, y: 331, rotation: 90 },
    { type: 'chair', x: 390, y: 331, rotation: 90 },
    { type: 'chair', x: 440, y: 306, rotation: 90 },
    { type: 'chair', x: 415, y: 306, rotation: 90 },

    // Room K
    { type: 'chair', x: 525, y: 306, rotation: 90 },
    { type: 'chair', x: 500, y: 356, rotation: 90 },
    { type: 'chair', x: 475, y: 356, rotation: 90 },
    { type: 'chair', x: 525, y: 331, rotation: 90 },
    { type: 'chair', x: 500, y: 331, rotation: 90 },
    { type: 'chair', x: 475, y: 331, rotation: 90 },
    { type: 'chair', x: 500, y: 306, rotation: 90 },
    { type: 'chair', x: 475, y: 306, rotation: 90 },

    // Room J
    { type: 'chair', x: 690, y: 356, rotation: 90 },
    { type: 'chair', x: 665, y: 356, rotation: 90 },
    { type: 'chair', x: 640, y: 306, rotation: 90 },
    { type: 'chair', x: 690, y: 331, rotation: 90 },
    { type: 'chair', x: 665, y: 331, rotation: 90 },
    { type: 'chair', x: 640, y: 331, rotation: 90 },
    { type: 'chair', x: 690, y: 306, rotation: 90 },
    { type: 'chair', x: 665, y: 306, rotation: 90 },

    // Room G
    { type: 'chair', x: 775, y: 306, rotation: 90 },
    { type: 'chair', x: 750, y: 356, rotation: 90 },
    { type: 'chair', x: 725, y: 356, rotation: 90 },
    { type: 'chair', x: 775, y: 331, rotation: 90 },
    { type: 'chair', x: 750, y: 331, rotation: 90 },
    { type: 'chair', x: 725, y: 331, rotation: 90 },
    { type: 'chair', x: 750, y: 306, rotation: 90 },
    { type: 'chair', x: 725, y: 306, rotation: 90 },
  ],
  room2: [
    // Room 201F
    { type: 'chair', x: 128, y: 200, rotation: 270 },
    { type: 'chair', x: 103, y: 200, rotation: 270 },
    { type: 'chair', x: 78, y: 200, rotation: 270 },
    { type: 'chair', x: 128, y: 175, rotation: 270 },
    { type: 'chair', x: 103, y: 175, rotation: 270 },
    { type: 'chair', x: 78, y: 175, rotation: 270 },
    { type: 'chair', x: 128, y: 150, rotation: 270 },
    { type: 'chair', x: 103, y: 150, rotation: 270 },
    
    // Room 201G
    { type: 'chair', x: 225, y: 200, rotation: 270 },
    { type: 'chair', x: 200, y: 200, rotation: 270 },
    { type: 'chair', x: 175, y: 200, rotation: 270 },
    { type: 'chair', x: 225, y: 175, rotation: 270 },
    { type: 'chair', x: 200, y: 175, rotation: 270 },
    { type: 'chair', x: 175, y: 175, rotation: 270 },
    { type: 'chair', x: 200, y: 150, rotation: 270 },
    { type: 'chair', x: 175, y: 150, rotation: 270 },

    // Room 201E
    { type: 'chair', x: 128, y: 409, rotation: 90 },
    { type: 'chair', x: 103, y: 409, rotation: 90 },
    { type: 'chair', x: 78, y: 359, rotation: 90 },
    { type: 'chair', x: 128, y: 384, rotation: 90 },
    { type: 'chair', x: 103, y: 384, rotation: 90 },
    { type: 'chair', x: 78, y: 384, rotation: 90 },
    { type: 'chair', x: 128, y: 359, rotation: 90 },
    { type: 'chair', x: 103, y: 359, rotation: 90 },

    // Room 201D
    { type: 'chair', x: 225, y: 359, rotation: 90 },
    { type: 'chair', x: 200, y: 409, rotation: 90 },
    { type: 'chair', x: 175, y: 409, rotation: 90 },
    { type: 'chair', x: 225, y: 384, rotation: 90 },
    { type: 'chair', x: 200, y: 384, rotation: 90 },
    { type: 'chair', x: 175, y: 384, rotation: 90 },
    { type: 'chair', x: 200, y: 359, rotation: 90 },
    { type: 'chair', x: 175, y: 359, rotation: 90 },

    // Room 301A
    { type: 'chair', x: 717, y: 254, rotation: 270 },
    { type: 'chair', x: 692, y: 254, rotation: 270 },
    { type: 'chair', x: 667, y: 254, rotation: 270 },
    { type: 'chair', x: 717, y: 229, rotation: 270 },
    { type: 'chair', x: 692, y: 229, rotation: 270 },
    { type: 'chair', x: 667, y: 229, rotation: 270 },
    { type: 'chair', x: 717, y: 204, rotation: 270 },
    { type: 'chair', x: 692, y: 204, rotation: 270 },

    // Room 301B
    { type: 'chair', x: 811, y: 254, rotation: 270 },
    { type: 'chair', x: 786, y: 254, rotation: 270 },
    { type: 'chair', x: 761, y: 254, rotation: 270 },
    { type: 'chair', x: 811, y: 229, rotation: 270 },
    { type: 'chair', x: 786, y: 229, rotation: 270 },
    { type: 'chair', x: 761, y: 229, rotation: 270 },
    { type: 'chair', x: 786, y: 204, rotation: 270 },
    { type: 'chair', x: 761, y: 204, rotation: 270 },

    // Room 301C
    { type: 'chair', x: 942, y: 254, rotation: 270 },
    { type: 'chair', x: 917, y: 254, rotation: 270 },
    { type: 'chair', x: 892, y: 254, rotation: 270 },
    { type: 'chair', x: 942, y: 229, rotation: 270 },
    { type: 'chair', x: 917, y: 229, rotation: 270 },
    { type: 'chair', x: 892, y: 229, rotation: 270 },
    { type: 'chair', x: 917, y: 204, rotation: 270 },
    { type: 'chair', x: 892, y: 204, rotation: 270 },

    // Room 301F
    { type: 'chair', x: 717, y: 461, rotation: 90 },
    { type: 'chair', x: 692, y: 461, rotation: 90 },
    { type: 'chair', x: 667, y: 411, rotation: 90 },
    { type: 'chair', x: 717, y: 436, rotation: 90 },
    { type: 'chair', x: 692, y: 436, rotation: 90 },
    { type: 'chair', x: 667, y: 436, rotation: 90 },
    { type: 'chair', x: 717, y: 411, rotation: 90 },
    { type: 'chair', x: 692, y: 411, rotation: 90 },

    // Room 301E
    { type: 'chair', x: 811, y: 411, rotation: 90 },
    { type: 'chair', x: 786, y: 461, rotation: 90 },
    { type: 'chair', x: 761, y: 461, rotation: 90 },
    { type: 'chair', x: 811, y: 436, rotation: 90 },
    { type: 'chair', x: 786, y: 436, rotation: 90 },
    { type: 'chair', x: 761, y: 436, rotation: 90 },
    { type: 'chair', x: 786, y: 411, rotation: 90 },
    { type: 'chair', x: 761, y: 411, rotation: 90 },

    // Room 301D
    { type: 'chair', x: 942, y: 411, rotation: 90 },
    { type: 'chair', x: 917, y: 461, rotation: 90 },
    { type: 'chair', x: 892, y: 461, rotation: 90 },
    { type: 'chair', x: 942, y: 436, rotation: 90 },
    { type: 'chair', x: 917, y: 436, rotation: 90 },
    { type: 'chair', x: 892, y: 436, rotation: 90 },
    { type: 'chair', x: 917, y: 411, rotation: 90 },
    { type: 'chair', x: 892, y: 411, rotation: 90 },

    // Room 301K
    { type: 'chair', x: 476, y: 89, rotation: 180 },
    { type: 'chair', x: 451, y: 89, rotation: 180 },
    { type: 'chair', x: 426, y: 39, rotation: 180 },
    { type: 'chair', x: 476, y: 64, rotation: 180 },
    { type: 'chair', x: 451, y: 64, rotation: 180 },
    { type: 'chair', x: 426, y: 64, rotation: 180 },
    { type: 'chair', x: 476, y: 39, rotation: 180 },
    { type: 'chair', x: 451, y: 39, rotation: 180 },

    // Room 301J
    { type: 'chair', x: 401, y: 212, rotation: 180 },
    { type: 'chair', x: 401, y: 237, rotation: 180 },
    { type: 'chair', x: 426, y: 212, rotation: 180 },
    { type: 'chair', x: 476, y: 237, rotation: 180 },
    { type: 'chair', x: 451, y: 237, rotation: 180 },
    { type: 'chair', x: 426, y: 237, rotation: 180 },
    { type: 'chair', x: 476, y: 212, rotation: 180 },
    { type: 'chair', x: 451, y: 212, rotation: 180 },

    // Room 301G
    { type: 'chair', x: 476, y: 514, rotation: 180 },
    { type: 'chair', x: 451, y: 514, rotation: 180 },
    { type: 'chair', x: 426, y: 514, rotation: 180 },
    { type: 'chair', x: 476, y: 489, rotation: 180 },
    { type: 'chair', x: 451, y: 489, rotation: 180 },
    { type: 'chair', x: 426, y: 489, rotation: 180 },
    { type: 'chair', x: 476, y: 464, rotation: 180 },
    { type: 'chair', x: 451, y: 464, rotation: 180 },

  ]
};

const smallTableAudienceStyleLayouts = {
  room1: [
    // Room A
    { type: 'chair', x: 195, y: 150, rotation: 270},
    { type: 'chair', x: 170, y: 150, rotation: 270},
    { type: 'chair', x: 145, y: 150, rotation: 270},
    { type: 'chair', x: 195, y: 125, rotation: 270},
    { type: 'chair', x: 170, y: 125, rotation: 270},
    { type: 'chair', x: 145, y: 125, rotation: 270},
    { type: 'chair', x: 195, y: 100, rotation: 270},
    { type: 'chair', x: 170, y: 100, rotation: 270},
    { type: 'small_table', x: 158, y: 46},

    // Room B
    { type: 'chair', x: 280, y: 150, rotation: 270 },
    { type: 'chair', x: 255, y: 150, rotation: 270 },
    { type: 'chair', x: 230, y: 150, rotation: 270 },
    { type: 'chair', x: 280, y: 125, rotation: 270 },
    { type: 'chair', x: 255, y: 125, rotation: 270 },
    { type: 'chair', x: 230, y: 125, rotation: 270 },
    { type: 'chair', x: 255, y: 100, rotation: 270 },
    { type: 'chair', x: 230, y: 100, rotation: 270 },
    { type: 'small_table', x: 260, y: 46},

    
    // Room C
    { type: 'chair', x: 440, y: 150, rotation: 270 },
    { type: 'chair', x: 415, y: 150, rotation: 270 },
    { type: 'chair', x: 390, y: 150, rotation: 270 },
    { type: 'chair', x: 440, y: 125, rotation: 270 },
    { type: 'chair', x: 415, y: 125, rotation: 270 },
    { type: 'chair', x: 390, y: 125, rotation: 270 },
    { type: 'chair', x: 440, y: 100, rotation: 270 },
    { type: 'chair', x: 415, y: 100, rotation: 270 },
    { type: 'small_table', x: 403, y: 46},


    // Room D
    { type: 'chair', x: 525, y: 150, rotation: 270 },
    { type: 'chair', x: 500, y: 150, rotation: 270 },
    { type: 'chair', x: 475, y: 150, rotation: 270 },
    { type: 'chair', x: 525, y: 125, rotation: 270 },
    { type: 'chair', x: 500, y: 125, rotation: 270 },
    { type: 'chair', x: 475, y: 125, rotation: 270 },
    { type: 'chair', x: 500, y: 100, rotation: 270 },
    { type: 'chair', x: 475, y: 100, rotation: 270 },
    { type: 'small_table', x: 505, y: 46},


    // Room E
    { type: 'chair', x: 690, y: 150, rotation: 270 },
    { type: 'chair', x: 665, y: 150, rotation: 270 },
    { type: 'chair', x: 640, y: 150, rotation: 270 },
    { type: 'chair', x: 690, y: 125, rotation: 270 },
    { type: 'chair', x: 665, y: 125, rotation: 270 },
    { type: 'chair', x: 640, y: 125, rotation: 270 },
    { type: 'chair', x: 690, y: 100, rotation: 270 },
    { type: 'chair', x: 665, y: 100, rotation: 270 },
    { type: 'small_table', x: 653, y: 46},


    // Room F
    { type: 'chair', x: 775, y: 150, rotation: 270 },
    { type: 'chair', x: 750, y: 150, rotation: 270 },
    { type: 'chair', x: 725, y: 150, rotation: 270 },
    { type: 'chair', x: 775, y: 125, rotation: 270 },
    { type: 'chair', x: 750, y: 125, rotation: 270 },
    { type: 'chair', x: 725, y: 125, rotation: 270 },
    { type: 'chair', x: 750, y: 100, rotation: 270 },
    { type: 'chair', x: 725, y: 100, rotation: 270 },
    { type: 'small_table', x: 755, y: 46},


    // Room N
    { type: 'chair', x: 195, y: 356, rotation: 90 },
    { type: 'chair', x: 170, y: 356, rotation: 90 },
    { type: 'chair', x: 145, y: 306, rotation: 90 },
    { type: 'chair', x: 195, y: 331, rotation: 90 },
    { type: 'chair', x: 170, y: 331, rotation: 90 },
    { type: 'chair', x: 145, y: 331, rotation: 90 },
    { type: 'chair', x: 195, y: 306, rotation: 90 },
    { type: 'chair', x: 170, y: 306, rotation: 90 },
    { type: 'small_table', x: 158, y: 393},

    // Room M
    { type: 'chair', x: 280, y: 306, rotation: 90 },
    { type: 'chair', x: 255, y: 356, rotation: 90 },
    { type: 'chair', x: 230, y: 356, rotation: 90 },
    { type: 'chair', x: 280, y: 331, rotation: 90 },
    { type: 'chair', x: 255, y: 331, rotation: 90 },
    { type: 'chair', x: 230, y: 331, rotation: 90 },
    { type: 'chair', x: 255, y: 306, rotation: 90 },
    { type: 'chair', x: 230, y: 306, rotation: 90 },
    { type: 'small_table', x: 260, y: 393},

    // Room L
    { type: 'chair', x: 440, y: 356, rotation: 90 },
    { type: 'chair', x: 415, y: 356, rotation: 90 },
    { type: 'chair', x: 390, y: 306, rotation: 90 },
    { type: 'chair', x: 440, y: 331, rotation: 90 },
    { type: 'chair', x: 415, y: 331, rotation: 90 },
    { type: 'chair', x: 390, y: 331, rotation: 90 },
    { type: 'chair', x: 440, y: 306, rotation: 90 },
    { type: 'chair', x: 415, y: 306, rotation: 90 },
    { type: 'small_table', x: 403, y: 393},

    // Room K
    { type: 'chair', x: 525, y: 306, rotation: 90 },
    { type: 'chair', x: 500, y: 356, rotation: 90 },
    { type: 'chair', x: 475, y: 356, rotation: 90 },
    { type: 'chair', x: 525, y: 331, rotation: 90 },
    { type: 'chair', x: 500, y: 331, rotation: 90 },
    { type: 'chair', x: 475, y: 331, rotation: 90 },
    { type: 'chair', x: 500, y: 306, rotation: 90 },
    { type: 'chair', x: 475, y: 306, rotation: 90 },
    { type: 'small_table', x: 505, y: 393},

    // Room J
    { type: 'chair', x: 690, y: 356, rotation: 90 },
    { type: 'chair', x: 665, y: 356, rotation: 90 },
    { type: 'chair', x: 640, y: 306, rotation: 90 },
    { type: 'chair', x: 690, y: 331, rotation: 90 },
    { type: 'chair', x: 665, y: 331, rotation: 90 },
    { type: 'chair', x: 640, y: 331, rotation: 90 },
    { type: 'chair', x: 690, y: 306, rotation: 90 },
    { type: 'chair', x: 665, y: 306, rotation: 90 },
    { type: 'small_table', x: 653, y: 393},

    // Room G
    { type: 'chair', x: 775, y: 306, rotation: 90 },
    { type: 'chair', x: 750, y: 356, rotation: 90 },
    { type: 'chair', x: 725, y: 356, rotation: 90 },
    { type: 'chair', x: 775, y: 331, rotation: 90 },
    { type: 'chair', x: 750, y: 331, rotation: 90 },
    { type: 'chair', x: 725, y: 331, rotation: 90 },
    { type: 'chair', x: 750, y: 306, rotation: 90 },
    { type: 'chair', x: 725, y: 306, rotation: 90 },
    { type: 'small_table', x: 755, y: 393},

  ],
  room2: [
  ]
};
const RoomLayout = () => {
  // Refs
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const nextIdRef = useRef(1);
  const layoutRef = useRef(null);

  // get walls with id
  const wallLayout1 = wallLayout.room1.map((item) => ({
    ...item,
    id: nextIdRef.current++,
  }));

  const wallLayout2 = wallLayout.room2.map((item) => ({
    ...item,
    id: nextIdRef.current++,
  }));

  // Room and layout state
  const [selectedRoom, setSelectedRoom] = useState('room1');
  const [roomStates, setRoomStates] = useState({
    room1: { ...INITIAL_ROOM_STATE_JFF, walls: wallLayout1 },
    room2: { ...INITIAL_ROOM_STATE_JKP, walls: wallLayout2 }
  });
  const [imageDimensions, setImageDimensions] = useState({ width: 800, height: 600 });

  // UI state
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [clipboardItem, setClipboardItem] = useState(null);
  // Constants
  const scaleMultiplier = 1.5;

  // Get current room's state
  const currentRoomState = roomStates[selectedRoom];
  const { furniture, chairNumber, tableNumber, smallTableNumber, walls } = currentRoomState;

  // Helper function to update current room state
  const updateCurrentRoomState = (updates) => {
    setRoomStates(prev => ({
      ...prev,
      [selectedRoom]: {
        ...prev[selectedRoom],
        ...updates
      }
    }));
  };

  useEffect(() => {
    const img = new Image();
    img.src = getRoomImage(selectedRoom);
    img.onload = () => {
      const newDimensions = {
        width: img.naturalWidth * scaleMultiplier * 0.55,
        height: img.naturalHeight * scaleMultiplier * 0.55
      };
      console.log('Setting new dimensions:', newDimensions);
      console.log('Original image dimensions:', {
        width: img.naturalWidth,
        height: img.naturalHeight
      });
      setImageDimensions(newDimensions);
    };
  }, [selectedRoom]);

  const svgRef = useRef(null);
  const {
    selectedIds,
    selectionBox,
    startSelection,
    updateSelection,
    endSelection,
    clearSelection,
    addToSelection,
    removeFromSelection,
    setSelectedIds,
    moveSelectedItems
  } = useSelectionManager();

  const handleAddFurniture = (type) => {
    // Get current counts
    const { furniture, chairNumber, tableNumber, smallTableNumber } = currentRoomState;

    // Check if we can add the furniture
    if (type === 'chair' && chairNumber <= 0) return;
    if (type === 'table' && tableNumber <= 0) return;
    if (type === 'small_table' && smallTableNumber <= 0) return;

    // Create new furniture item
    const newFurniture = {
      id: nextIdRef.current,
      type,
      x: 50,
      y: 50,
      rotation: 0
    };

    // Update the current room's state with new furniture and updated counts
    updateCurrentRoomState({
      furniture: [...furniture, newFurniture],
      chairNumber: type === 'chair' ? chairNumber - 1 : chairNumber,
      tableNumber: type === 'table' ? tableNumber - 1 : tableNumber,
      smallTableNumber: type === 'small_table' ? smallTableNumber - 1 : smallTableNumber
    });

    nextIdRef.current += 1;
  };

  const handleDeleteFurniture = useCallback(() => {
    const selectedIdsArray = Array.from(selectedIds);
    const { furniture, chairNumber, tableNumber, smallTableNumber } = currentRoomState;

    // Count furniture to be returned to inventory
    const updatedCounts = selectedIdsArray.reduce((acc, id) => {
      const item = furniture.find(f => f.id === id);
      if (item) {
        if (item.type === 'chair') acc.chairNumber++;
        if (item.type === 'table') acc.tableNumber++;
        if (item.type === 'small_table') acc.smallTableNumber++;
      }
      return acc;
    }, {
      chairNumber,
      tableNumber,
      smallTableNumber
    });

    // Update room state
    updateCurrentRoomState({
      furniture: furniture.filter(item => !selectedIdsArray.includes(item.id)),
      chairNumber: updatedCounts.chairNumber,
      tableNumber: updatedCounts.tableNumber,
      smallTableNumber: updatedCounts.smallTableNumber
    });

    clearSelection();
  }, [currentRoomState, selectedIds, clearSelection, updateCurrentRoomState]);

  const handleRotateFurniture = useCallback(() => {
    const { furniture } = currentRoomState;
    const selectedIdsArray = Array.from(selectedIds);

    updateCurrentRoomState({
      furniture: furniture.map(item => {
        if (selectedIdsArray.includes(item.id)) {
          return {
            ...item,
            rotation: ((item.rotation || 0) + 90) % 360
          };
        }
        return item;
      })
    });
  }, [currentRoomState, selectedIds, updateCurrentRoomState]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const { furniture, chairNumber, tableNumber, smallTableNumber } = currentRoomState;

      // Delete/Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedIds.size > 0) {
          handleDeleteFurniture();
        }
      }

      // Copy (Ctrl/Cmd + C)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedIds.size > 0) {
          e.preventDefault();
          const itemsToCopy = furniture
            .filter(item => selectedIds.has(item.id))
            .map(item => ({
              ...item,
              x: item.x + 20,
              y: item.y + 20
            }));
          setClipboardItem(itemsToCopy);
        }
      }

      // Paste (Ctrl/Cmd + V)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (clipboardItem) {
          e.preventDefault();

          const itemsToPaste = Array.isArray(clipboardItem) ? clipboardItem : [clipboardItem];

          // Count required furniture
          const requiredCounts = itemsToPaste.reduce((acc, item) => {
            acc[item.type]++;
            return acc;
          }, { chair: 0, table: 0, small_table: 0 });

          // Check if we have enough furniture
          if (requiredCounts.chair <= chairNumber &&
            requiredCounts.table <= tableNumber &&
            requiredCounts.small_table <= smallTableNumber) {

            // Create new furniture with new IDs
            const newFurniture = itemsToPaste.map(item => ({
              ...item,
              id: nextIdRef.current++
            }));

            // Update room state
            updateCurrentRoomState({
              furniture: [...furniture, ...newFurniture],
              chairNumber: chairNumber - requiredCounts.chair,
              tableNumber: tableNumber - requiredCounts.table,
              smallTableNumber: smallTableNumber - requiredCounts.small_table
            });

            // Select newly pasted items
            const newSelection = new Set(newFurniture.map(item => item.id));
            setSelectedIds(newSelection);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    currentRoomState,
    selectedIds,
    clipboardItem,
    handleDeleteFurniture,
    setSelectedIds,
    updateCurrentRoomState
  ]);

  const handleClearLayout = () => {
    updateCurrentRoomState({
      furniture: [],
      chairNumber: getInitialRoomState(selectedRoom).chairNumber,
      tableNumber: getInitialRoomState(selectedRoom).tableNumber,
      smallTableNumber: getInitialRoomState(selectedRoom).smallTableNumber
    });
    clearSelection();
  };

  const handleSaveAllLayouts = async () => {
    try {
      const layoutSaver = new LayoutSaver(layoutRef);
      await layoutSaver.saveAllLayouts(Object.keys(roomStates), setSelectedRoom);
    } catch (error) {
      console.error('Save error:', error);
      alert('There was an error saving the layouts. Please try again.');
    }
  };

  const handleMouseDown = (id) => (e) => {
    e.stopPropagation(); // Prevent selection box from starting
    const item = furniture.find(item => item.id === id);
    const svgRect = e.currentTarget.closest('svg').getBoundingClientRect();
    const clickX = e.clientX - svgRect.left;
    const clickY = e.clientY - svgRect.top;

    if (e.shiftKey) {
      // Shift-click: add or remove from current selection
      if (selectedIds.has(id)) {
        removeFromSelection(id);
      } else {
        addToSelection(id);
      }
    } else if (!selectedIds.has(id)) {
      // Only reset selection if clicking an unselected item
      const newSelection = new Set([id]);
      setSelectedIds(newSelection);
    }


    dragOffsetRef.current = {
      x: clickX - item.x,
      y: clickY - item.y
    };

    setDraggedItem(id);
  };

  const handleWallMouseClick = (id) => (e) => {
    e.stopPropagation(); // Prevent selection box from starting
    updateCurrentRoomState({
      walls: walls.map(item => {
        if (item.id === id) {
          return {
            ...item,
            visible: !item.visible
          };
        }
        return item;
      })
    });
  }

  const handleSvgMouseDown = (e) => {
    if (e.target === e.currentTarget || e.target.tagName === 'image') {
      clearSelection(); // Clear existing selection
      startSelection(e, svgRef);
    }
  };

  const handleMouseMove = (e) => {
    if (draggedItem !== null) {
      const svgRect = e.currentTarget.getBoundingClientRect();
      let x = e.clientX - svgRect.left - dragOffsetRef.current.x;
      let y = e.clientY - svgRect.top - dragOffsetRef.current.y;

      // Constrain to boundaries
      x = Math.max(0, Math.min(x, imageDimensions.width - 50));
      y = Math.max(0, Math.min(y, imageDimensions.height - 50));

      if (selectedIds.size > 1) {
        // Move multiple items
        const draggedFurniture = furniture.find(item => item.id === draggedItem);
        if (draggedFurniture) {
          const dx = x - draggedFurniture.x;
          const dy = y - draggedFurniture.y;
          const updatedFurniture = moveSelectedItems(dx, dy, furniture);
          updateCurrentRoomState({ furniture: updatedFurniture });
        }
      } else {
        // Move single item
        const updatedFurniture = furniture.map(item =>
          item.id === draggedItem ? { ...item, x, y } : item
        );
        updateCurrentRoomState({ furniture: updatedFurniture });
      }
    } else {
      updateSelection(e, svgRef);
    }
  };

  const handleMouseUp = () => {
    if (selectionBox) {
      endSelection(furniture);
    }
    setDraggedItem(null);
  };

  const loadDefaultLayout = () => {
    // Get default layout for the current room
    const defaultFurniture = defaultLayouts[selectedRoom] || [];
    const defaultWall = wallLayout[selectedRoom] || [];
  
    // Count the number of each type of furniture in the default layout
    const usedCounts = defaultFurniture.reduce(
      (acc, item) => {
        if (item.type === 'chair') acc.chairNumber++;
        if (item.type === 'table') acc.tableNumber++;
        if (item.type === 'small_table') acc.smallTableNumber++;
        return acc;
      },
      { chairNumber: 0, tableNumber: 0, smallTableNumber: 0 }
    );
  
    // Assign IDs to default furniture
    const furnitureWithIds = defaultFurniture.map((item) => ({
      ...item,
      id: nextIdRef.current++,
    }));
  
    // Update room state with the new furniture and adjusted inventory counts
    updateCurrentRoomState({
      furniture: furnitureWithIds,
      chairNumber: getInitialRoomState(selectedRoom).chairNumber - usedCounts.chairNumber,
      tableNumber: getInitialRoomState(selectedRoom).tableNumber - usedCounts.tableNumber,
      smallTableNumber: getInitialRoomState(selectedRoom).smallTableNumber - usedCounts.smallTableNumber,
    });
  };  

  const applyAudienceStyleLayout = () => {
    // Get layout for the selected room
    const audienceLayout = audienceStyleLayouts[selectedRoom] || [];
  
    // Assign IDs to furniture
    const furnitureWithIds = audienceLayout.map((item) => ({
      ...item,
      id: nextIdRef.current++,
    }));
  
    // Count the furniture used
    const usedCounts = audienceLayout.reduce(
      (acc, item) => {
        if (item.type === 'chair') acc.chairNumber++;
        if (item.type === 'table') acc.tableNumber++;
        if (item.type === 'small_table') acc.smallTableNumber++;
        return acc;
      },
      { chairNumber: 0, tableNumber: 0, smallTableNumber: 0 }
    );
  
    // Update room state
    updateCurrentRoomState({
      furniture: furnitureWithIds,
      chairNumber: getInitialRoomState(selectedRoom).chairNumber - usedCounts.chairNumber,
      tableNumber: getInitialRoomState(selectedRoom).tableNumber - usedCounts.tableNumber,
      smallTableNumber: getInitialRoomState(selectedRoom).smallTableNumber - usedCounts.smallTableNumber,
    });
  };
  
  const applySmallTableAudienceStyleLayout = () => {
    // Get layout for the selected room
    const smallTableLayout = smallTableAudienceStyleLayouts[selectedRoom] || [];
  
    // Assign IDs to furniture
    const furnitureWithIds = smallTableLayout.map((item) => ({
      ...item,
      id: nextIdRef.current++,
    }));
  
    // Count the furniture used
    const usedCounts = smallTableLayout.reduce(
      (acc, item) => {
        if (item.type === 'chair') acc.chairNumber++;
        if (item.type === 'table') acc.tableNumber++;
        if (item.type === 'small_table') acc.smallTableNumber++;
        return acc;
      },
      { chairNumber: 0, tableNumber: 0, smallTableNumber: 0 }
    );
  
    // Update room state
    updateCurrentRoomState({
      furniture: furnitureWithIds,
      chairNumber: getInitialRoomState(selectedRoom).chairNumber - usedCounts.chairNumber,
      tableNumber: getInitialRoomState(selectedRoom).tableNumber - usedCounts.tableNumber,
      smallTableNumber: getInitialRoomState(selectedRoom).smallTableNumber - usedCounts.smallTableNumber,
    });
  };
  const Sidebar = ({ onAddFurniture }) => {
    const toggleSidebar = () => {
      setIsCollapsed(!isCollapsed);
    };

    return (
      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <button className="sidebar-toggle" onClick={toggleSidebar}>
          {isCollapsed ? (
            <div className="hamburger-menu">☰</div>
          ) : (
            '✕'
          )}
        </button>

        <div className="sidebar-content-wrapper">
          <div className="sidebar-content">
            {/* Layout Selection Section */}
            <div className="sidebar-section customize-section">
              <h3 className="section-title">Select a Layout</h3>
              <button className="layout-button" onClick={loadDefaultLayout}>
                <span>Default Layout</span>
              </button>
              <button className="layout-button" onClick={applyAudienceStyleLayout}>
                <span>Audience Style</span>
              </button>
              {selectedRoom === 'room1' && <button className="layout-button" onClick={applySmallTableAudienceStyleLayout}>
                <span>Small Table Audience Style</span>
              </button>}
            </div>

            {/* Furniture Menu Section */}
            <div className="sidebar-section customize-section">
              <h3 className="section-title">Or, add furniture to customize</h3>
              <button
                onClick={() => onAddFurniture('chair')}
                className="furniture-button"
              >
                <div className="furniture-button-title">Chairs</div>
                <img src={chair} alt="chairs" />
                <div className="furniture-count">
                  <span className="furniture-count-number">{chairNumber}</span>
                  <span className="furniture-count-text">remaining</span>
                </div>
              </button>

              {selectedRoom === 'room1' && <button
                onClick={() => onAddFurniture('small_table')}
                className="furniture-button"
              >
                <div className="furniture-button-title">Small Tables</div>
                <img src={smallTable} alt="smallTable" />
                <div className="furniture-count">
                  <span className="furniture-count-number">{smallTableNumber}</span>
                  <span className="furniture-count-text">remaining</span>
                </div>
              </button>}

              <button
                onClick={() => onAddFurniture('table')}
                className="furniture-button"
              >
                <div className="furniture-button-title">Conference Tables</div>
                <img src={conferenceTable} alt="conference tables" />
                <div className="furniture-count">
                  <span className="furniture-count-number">{tableNumber}</span>
                  <span className="furniture-count-text">remaining</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`room-layout-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar onAddFurniture={handleAddFurniture} />
      {/* <ControlPanel
        selectedItem={selectedFurniture}
        onRotate={handleRotate}
      /> */}
      {/* Title Container */}

      <div style={{ paddingLeft: '50px' }}>
        <h2>
          {selectedRoom === 'room1' ? 'Room Layout for JFF Basement' :
            selectedRoom === 'room2' ? 'Room Layout for JKP' :
                'Room Layout'}
        </h2>
      </div>
      {/* Interactable Layout Container */}
      <div
        ref={layoutRef}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 20vh)', // Adjust this value to account for the height of the title and padding
        }}
      >
        <div>
          <div className="action-buttons">
            {/* TODO: fix CSS for these buttons and add icons */}
            <button onClick={handleDeleteFurniture} className="delete-rotate-button">
              <img src={deleteIcon} alt='Delete Icon'/>
              <span>Delete</span>
            </button>
            <button onClick={handleRotateFurniture} className="delete-rotate-button">
              <img src={rotateIcon} alt='Rotate Icon'/>
              <span>Rotate</span>
            </button>
            <button onClick={handleClearLayout} className="clear-layout-button">
              <span>Clear Layout</span>
            </button>
            <button onClick={handleSaveAllLayouts} className="download-layouts-button">
              <span>Download Layouts</span>
            </button>
          </div>

          <svg
            ref={svgRef}
            id="room-layout-svg"
            width={imageDimensions.width}
            height={imageDimensions.height}
            onMouseDown={handleSvgMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <image
              href={getRoomImage(selectedRoom)}
              x="0"
              y="0"
              width={imageDimensions.width}
              height={imageDimensions.height}
              preserveAspectRatio="xMidYMid slice"
            />
            <rect
              x="0"
              y="0"
              width={imageDimensions.width}
              height={imageDimensions.height}
              fill="none"
              stroke="black"
            />
            {walls.map((item) => {
              // Calculate center point offsets based on wall type
              let centerX, centerY;
              if (item.type === 'walljff') {
                centerX = 5; // half of width (10)
                centerY = 75; // half of height (149)
              } else { // walljkp
                centerX = 5; // half of width (10)
                centerY = 74; // half of height (149)
              }

              return (
                <g
                  key={item.id}
                  transform={`
                    translate(${item.x}, ${item.y})
                    translate(${centerX * 0.65 * scaleMultiplier}, ${centerY * 0.65 * scaleMultiplier})
                    rotate(${item.rotation || 0})
                    translate(${-centerX * 0.65 * scaleMultiplier}, ${-centerY * 0.65 * scaleMultiplier})
                    scale(${0.65 * scaleMultiplier})
                  `}
                  onMouseDown={handleWallMouseClick(item.id)}
                  style={{
                    cursor: 'pointer',
                    outline: selectedIds.has(item.id) ? '2px solid blue' : 'none'
                  }}
                >
                  {item.type === 'walljff' ? (item.visible === true ? <WallJFF /> : <DashedWallJFF />) : (item.visible === true ? <WallJKP /> : <DashedWallJKP />) }
                </g>
              );
            })}
            
            {furniture.map((item) => {
              // Calculate center point offsets based on furniture type
              let centerX, centerY;
              if (item.type === 'table') {
                centerX = 18; // half of width (36)
                centerY = 42; // half of height (84)
              } else if (item.type === 'small_table') {
                centerX = 18; // half of width (36)
                centerY = 18; // half of height (36)
              } else { // chair
                centerX = 11; // half of chair width (~22)
                centerY = 11; // half of chair height (~22)
              }

              return (
                <g
                  key={item.id}
                  transform={`
                    translate(${item.x}, ${item.y})
                    translate(${centerX * 0.65 * scaleMultiplier}, ${centerY * 0.65 * scaleMultiplier})
                    rotate(${item.rotation || 0})
                    translate(${-centerX * 0.65 * scaleMultiplier}, ${-centerY * 0.65 * scaleMultiplier})
                    scale(${0.65 * scaleMultiplier})
                  `}
                  onMouseDown={handleMouseDown(item.id)}
                  style={{
                    cursor: 'move',
                    outline: selectedIds.has(item.id) ? '2px solid blue' : 'none'
                  }}
                >
                  {item.type === 'table' ? <ConferenceTable /> : (item.type === 'chair' ? <Chair /> : <SmallTable />)}
                </g>
              );
            })}

            
            <SelectionBoxView selectionBox={selectionBox} />
          </svg>
        </div>
        <InstructPopup />
        {/* Room Selector at the bottom right */}
        <div className="room-selector-container">
          <RoomSelector
            onRoomChange={setSelectedRoom}
            onRoomChangeConfirmed={handleClearLayout}
            currentRoom={selectedRoom}
          />
        </div>
      </div>
      {/* Save Layout Button Outside the Capture Area */}


      {/* <button
        onClick={handleSaveLayout}
        style={{
          position: 'absolute',
          top: '80px',
          right: '30px',
          backgroundColor: '#4CAF50',
          color: 'white',
          padding: '10px',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer'
        }}
      >
        Save Layout
      </button> */}


    </div>
  );
};

export default RoomLayout;
